export interface DeletionPreviewInput {
  staffAccount: boolean
  loginKept: boolean
  suspended: boolean
  liveStreams: number
  shopifyLinkedBoards: number
  scheduledRunAt: string | null
  deletionStatus: string | null
}

export interface DeleteDialogCopy {
  title: string
  lead: string
  alreadyPending: string | null
  staffCallout: string[]
  liveNote: string | null
  blocked: string | null
  deletedItems: string[]
  keptItems: string[]
  confirmLabel: string
  busyLabel: string
}

export const DIALOG_LOADING = 'Checking what this deletion will do...'
export const DIALOG_PREVIEW_ERROR = 'Could not load what this deletion would do. Close this and try again.'

const LEAD = 'The deletion starts 24 hours after you confirm. Until then nothing is deleted or blocked, and any superuser can cancel it on this page.'

const STAFF_SHOPIFY_BLOCKED = 'This staff account has merch boards linked to a Shopify store. Moving Shopify stores is not built yet, so it cannot be deleted yet.'

const KEPT_RECORD = 'A minimal deletion record: when it was requested, who requested it, whether the account was suspended, how each step went, and final usage totals. It holds no email address.'
const KEPT_BILLING = 'Billing records in Stripe (invoices and the customer record), kept for tax and accounting duties.'
const CONNECTIONS = 'Their Twitch and YouTube connections. YouTube data is purged when the deletion starts.'
const MEMBERSHIPS = 'Their place as a member of other people\'s streams.'
const SUBSCRIPTION = 'Any paid subscription is cancelled immediately.'

export const runningNotice = (label: string, status: string): string =>
  `A deletion of ${label} is already running (status ${status}). Track it under Deletion requests.`

export const deleteDialogCopy = (preview: DeletionPreviewInput, who: string, scheduledRunAtText: string): DeleteDialogCopy => {
  const title = `Are you sure you want to delete ${who}?`
  let alreadyPending: string | null = null
  if (preview.deletionStatus != null && preview.deletionStatus !== '') {
    alreadyPending = runningNotice(who, preview.deletionStatus)
  } else if (preview.scheduledRunAt != null && preview.scheduledRunAt !== '') {
    alreadyPending = `A deletion of ${who} is already scheduled. It starts after ${scheduledRunAtText}.`
  }
  const live = preview.liveStreams > 0

  if (preview.staffAccount) {
    return {
      title,
      lead: LEAD,
      alreadyPending,
      staffCallout: [
        'This is a staff account (superuser or admin). Its streams, Quick Overlays, merch boards and photos move to your account instead of being deleted. Its FlightDeck roles are removed and its StagePilot login is kept: remove the person in Auth0 yourself to finish.',
        'Overlay links for the moved streams and Quick Overlays stop working. Copy fresh links from your own pages after the move.',
        'YouTube results gathered through this account\'s YouTube connection are purged, including in the moved streams.',
        'Shopify apps registered to this account do not move yet. Change each app\'s owner email to yours on the Admin page first.'
      ],
      liveNote: live ? 'A stream this account owns is live now. The deletion waits until it ends.' : null,
      blocked: preview.shopifyLinkedBoards > 0 ? STAFF_SHOPIFY_BLOCKED : null,
      deletedItems: [
        CONNECTIONS,
        'Their usage history.',
        'Their profile picture.',
        MEMBERSHIPS,
        SUBSCRIPTION
      ],
      keptItems: [KEPT_RECORD, KEPT_BILLING, 'Their StagePilot login. You remove it in Auth0.'],
      confirmLabel: 'Schedule deletion',
      busyLabel: 'Scheduling'
    }
  }

  return {
    title,
    lead: LEAD,
    alreadyPending,
    staffCallout: [],
    liveNote: live ? 'A stream this account owns is live now. It is ended when the deletion starts.' : null,
    blocked: null,
    deletedItems: [
      'Every stream they own and everything in it: polls, tug of wars, scoreboards, giveaways, votes, members and stream links. Live streams are ended first. Streams they own are deleted even when other people are members of them.',
      'Their overlays: chat overlays, emote walls, Message tickers and merch boards.',
      'Images they uploaded, and their profile picture.',
      'Their overlay links and short links.',
      'Their usage history.',
      CONNECTIONS,
      MEMBERSHIPS,
      preview.loginKept
        ? 'Their FlightDeck access. They also use another StagePilot app, so their StagePilot login is kept and only FlightDeck is removed.'
        : 'Their StagePilot login, so they can no longer sign in.',
      SUBSCRIPTION
    ],
    keptItems: [KEPT_RECORD, KEPT_BILLING],
    confirmLabel: 'Schedule deletion',
    busyLabel: 'Scheduling'
  }
}

export const scheduledNotice = (label: string, runAtText: string): string =>
  `Deletion of ${label} is scheduled. It starts after ${runAtText}. Nothing has been deleted yet, and any superuser can cancel it under Scheduled deletions.`

export const cancelConfirmCopy = (label: string): string =>
  `Cancel the scheduled deletion of ${label}? Nothing has been deleted yet.`

export const cancelledNotice = (label: string): string =>
  `The scheduled deletion of ${label} was cancelled. Nothing was deleted.`

export const SCHEDULED_CARD_HEADING = 'Scheduled deletions'
export const SCHEDULED_CARD_HELP = 'A deletion starts 24 hours after a superuser confirms it. Until then nothing is deleted, and any superuser can cancel it here.'
export const SCHEDULED_CARD_EMPTY = 'No deletions are scheduled.'

export const kindLabel = (staffAccount: boolean): string => (staffAccount ? 'Staff account' : 'Customer')
export const scheduledByLabel = (requestedByMe: boolean): string => (requestedByMe ? 'You' : 'Another superuser')

const hasKey = (obj: Record<string, string>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(obj, key)

const STOP_REASON_COPY: Record<string, string> = {
  'requester-not-superuser': 'Stopped: the superuser who scheduled it no longer has superuser access. Nothing was deleted.',
  'receiving-account-unavailable': 'Stopped: the account that would receive this staff account\'s things is gone or being deleted. Nothing was deleted.',
  'target-account-missing': 'Stopped: the FlightDeck account no longer exists. Nothing was deleted.',
  'target-kind-changed': 'Stopped: the account became staff, or stopped being staff, after it was scheduled. Nothing was deleted. Schedule it again if it is still wanted.',
  'suspended-review-required': 'Stopped: the account was suspended after it was scheduled, and the legal review was not confirmed. Nothing was deleted.',
  'shopify-transfer-pending': 'Stopped: this staff account has merch boards linked to a Shopify store, and moving stores is not built yet. Nothing was deleted.',
  'already-deleting': 'Stopped: a deletion of this account was already running. Nothing new was started.'
}

export const STOP_REASON_SLUGS = Object.freeze(Object.keys(STOP_REASON_COPY))

export const stopReasonCopy = (slug: string | null | undefined): string =>
  (slug != null && hasKey(STOP_REASON_COPY, slug) ? STOP_REASON_COPY[slug] : 'Stopped. Nothing was deleted.')

export interface ScheduledRowInput {
  status: string
  heldReason?: string | null
  stopReason?: string | null
}

export const HELD_LIVE_STREAM_COPY = 'Waiting: a stream this account owns is live. The deletion starts after it ends.'

export const scheduledStatusCopy = (row: ScheduledRowInput): string => {
  if (row.status === 'STOPPED') return stopReasonCopy(row.stopReason)
  if (row.heldReason === 'live-stream') return HELD_LIVE_STREAM_COPY
  return 'Scheduled'
}

export const scheduledTopLine = (n: number): string | null => {
  if (!(n > 0)) return null
  return n === 1
    ? '1 account deletion is scheduled. It can be cancelled until it starts.'
    : `${n} account deletions are scheduled. Each can be cancelled until it starts.`
}

export const DELETIONS_CARD_HELP = 'Account deletions that have started, and how each step went, newest first. YouTube data is purged when a deletion starts; the rest runs in the background. A failed or stalled run carries the needs-action chip and can be retried here (every step is safe to repeat). Records hold no email, so an account whose data is already gone reads "account removed".'

export interface DeletionRowInput {
  status: string
  staffAccount?: boolean | null
  loginKept?: boolean | null
}

export const deletionRowNote = (row: DeletionRowInput): string | null => {
  if (row.staffAccount === true) {
    return row.status === 'COMPLETE'
      ? 'Remove this person in Auth0 to finish.'
      : 'Staff account: its things move to the superuser who scheduled it.'
  }
  if (row.loginKept === true) return 'Login kept: they use another StagePilot app.'
  return null
}

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
  STOPPED: 'Stopped'
}

export const deletionStatusLabel = (status: string): string =>
  (hasKey(STATUS_LABELS, status) ? STATUS_LABELS[status] : status)

export const DELETE_BUTTON_TITLE = 'Schedule this account\'s deletion (a confirm step follows; it starts 24 hours later)'
export const DELETE_BUTTON_SCHEDULED_TITLE = 'A deletion of this account is scheduled. Cancel it under Scheduled deletions.'
export const DELETE_BUTTON_SCHEDULED_LABEL = 'Scheduled'

export const ALL_FIXED_COPY: readonly string[] = Object.freeze([
  DIALOG_LOADING,
  DIALOG_PREVIEW_ERROR,
  LEAD,
  STAFF_SHOPIFY_BLOCKED,
  KEPT_RECORD,
  KEPT_BILLING,
  CONNECTIONS,
  MEMBERSHIPS,
  SUBSCRIPTION,
  SCHEDULED_CARD_HEADING,
  SCHEDULED_CARD_HELP,
  SCHEDULED_CARD_EMPTY,
  HELD_LIVE_STREAM_COPY,
  DELETIONS_CARD_HELP,
  DELETE_BUTTON_TITLE,
  DELETE_BUTTON_SCHEDULED_TITLE,
  DELETE_BUTTON_SCHEDULED_LABEL,
  ...Object.values(STOP_REASON_COPY),
  ...Object.values(STATUS_LABELS)
])
